import './index.scss';

import { cloneElement, Component, ReactElement } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { BraxButton, BraxToasts, BraxToastsType, BraxTooltip } from 'design-system';

import t from '../../../utils/i18n';
import handleError from '../../../utils/errors';
import { withContexts } from '../../../utils/with-context';
import User from '../../../view-model/users/User';
import NotificationSummary from '../../../view-model/users/NotificationSummary';
import notificationsService from '../../../services/notifications';
import UserContext from '../../../contexts/User';

export type AppBarNotificationsProps = RouteComponentProps & {
    toasts: BraxToastsType,
    user: User,
    children: ReactElement,
    onShow: () => void,
    onClose: () => void
};

type AppBarNotificationsState = {
    open: boolean,
    notifications: NotificationSummary[],
    hasUnread: boolean
};

export default withRouter(withContexts({
    toasts: BraxToasts,
    user: UserContext
}, class AppBarNotifications extends Component<AppBarNotificationsProps, AppBarNotificationsState> {

    state: AppBarNotificationsState = {
        open: this.canBeShown(),
        notifications: [],
        hasUnread: false
    };

    loaded = false;

    rendered = this.props.location.pathname.startsWith('/notifications');

    unsubscribe?: () => void;

    componentDidMount() {
        if (this.state.open)
            this.load();
        else if (this.rendered)
            this.close();

        this.unsubscribe = notificationsService.subscribe(hash => this.setState(prevState => {
            const notifications = prevState.notifications.map(notification => notification.hash === hash ? { ...notification, read: true } : notification);

            return { notifications, hasUnread: notifications.findIndex(notification => !notification.read) !== -1 };
        }));
    }

    componentDidUpdate(prevProps: AppBarNotificationsProps) {
        if (prevProps.location.pathname !== this.props.location.pathname)
            if (this.state.open) {
                this.close();
            } else if (!this.rendered && this.canBeShown()) {
                this.setState({ open: true });

                if (!this.loaded)
                    this.load();
            }
    }

    componentWillUnmount() {
        this.unsubscribe?.();
    }

    async load() {
        this.loaded = true;

        try {
            const response = await notificationsService.getNotifications(this.props.user.id);

            const hasUnread = response.result.findIndex(notification => !notification.read) !== -1;

            this.setState({ hasUnread, notifications: response.result });

            if (hasUnread)
                this.props.onShow();
            else
                this.props.onClose();
        } catch (error) {
            this.props.toasts.create({ type: 'destructive', ...handleError(error) });
        }
    }

    canBeShown() {
        return !this.props.location.pathname.startsWith('/editor');
    }

    close() {
        this.rendered = true;

        this.setState({ open: false });

        this.props.onClose();
    }

    render() {
        const children = cloneElement(this.props.children, { 'notification-badge': `${this.state.hasUnread}` });

        return !this.rendered && this.state.open && this.state.hasUnread ? (<>
            <div className="overlay" onClick={() => this.close()} />

            <BraxTooltip
                open
                className={`notifications-tooltip ${this.state.hasUnread ? 'has-notification' : ''}`}
                placement="right-end"
                tooltip={
                    <div>
                        <h3>{ t('Notificações') }</h3>

                        { this.state.notifications.map(notification => (
                            <div key={notification.id} className="tooltip-card">
                                { notification.coverUrl ? (
                                    <img src={notification.coverUrl} alt={t('Imagem de capa da notificação')} />
                                ) : null }

                                <p className="complementary-title">{ notification.title }</p>

                                <p>{ notification.description }</p>

                                <div className={`badge ${notification.read ? 'read' : ''}`}>
                                    <div />

                                    <span className="hint">{ t(notification.read ? 'Lido' : 'Novo') }</span>
                                </div>

                                <Link to={`/notifications/${notification.id}`}>
                                    <BraxButton size="md">{ t('Ver') }</BraxButton>
                                </Link>
                            </div>
                        )) }
                    </div>
                }
            >
                { children }
            </BraxTooltip>
        </>) : (
            children
        );
    }

}));
